import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Globe, MapPin, Phone, Mail } from "lucide-react";
import { Reveal } from "./Reveal";

const inputCls =
    "w-full border border-line bg-transparent px-5 py-4 font-sans text-base text-ink placeholder:text-moss/50 outline-none transition-colors duration-300 focus:border-clay";

const FORMSPREE_ENDPOINT = (process.env.REACT_APP_FORMSPREE_ENDPOINT || "").trim();

export const QuoteForm = () => {
    const [form, setForm] = useState({ name: "", phone: "", type: "Life Insurance", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const confirm = () => {
        toast.success("Request received. Our advisor will call you within one working day.", {
            description: `Thank you, ${form.name}. We have noted your interest in ${form.type}.`,
        });
        setForm({ name: "", phone: "", type: "Life Insurance", message: "" });
    };

    const payload = () => ({
        name: form.name,
        phone: form.phone,
        insurance_type: form.type,
        message: form.message,
        _gotcha: form._gotcha || "",
    });

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const gotcha = e.target._gotcha ? e.target._gotcha.value : "";
        setSubmitting(true);
        try {
            if (FORMSPREE_ENDPOINT) {
                const res = await fetch(FORMSPREE_ENDPOINT, {
                    method: "POST",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify(payload()),
                });
                if (!res.ok) throw new Error(`Submission failed (${res.status})`);
            } else {
                const res = await fetch("/sendmail.php", {
                    method: "POST",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify({ ...payload(), _gotcha: gotcha }),
                });
                const data = await res.json().catch(() => null);
                if (!res.ok || !data?.ok) throw new Error("Mail server unavailable");
            }
            confirm();
        } catch (err) {
            toast.error("Could not send right now. Please call or WhatsApp us at +91 99941 58837.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="quote" data-testid="quote-section" className="border-t border-line bg-white/40">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-32 md:px-12 lg:grid-cols-12">
                <Reveal className="lg:col-span-5">
                    <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-clay">Free consultation</p>
                    <h2 className="font-serif text-4xl font-medium leading-none tracking-tight md:text-6xl">
                        Talk to an <span className="italic text-clay">advisor today</span>
                    </h2>
                    <p className="mt-8 max-w-sm text-base leading-relaxed text-moss">
                        Tell us what you want to protect. A PolicyOne advisor will call you back, compare options across insurers, and explain everything — in Tamil or English.
                    </p>
                    <div className="mt-12 space-y-6">
                        <div className="flex items-start gap-4">
                            <MapPin size={18} className="mt-1 text-clay" />
                            <p className="text-sm leading-relaxed text-moss" data-testid="contact-address">
                                1/2B Raviram Complex, Madukkarai Road,<br />
                                Opp. Old Dinamalar Office, Sundarapuram,<br />
                                Coimbatore, Tamil Nadu 641 021
                            </p>
                        </div>
                        <div className="flex items-start gap-4">
                            <Phone size={18} className="mt-1 text-clay" />
                            <div className="flex flex-col gap-1.5">
                                <a href="tel:+919994158837" data-testid="contact-phone" className="text-sm text-moss transition-colors duration-300 hover:text-clay">
                                    +91 99941 58837
                                </a>
                                <a href="tel:+919788800043" data-testid="contact-phone-2" className="text-sm text-moss transition-colors duration-300 hover:text-clay">
                                    +91 97888 00043
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <Mail size={18} className="text-clay" />
                            <a href="mailto:care@policy1.co.in" data-testid="contact-email" className="text-sm text-moss transition-colors duration-300 hover:text-clay">
                                care@policy1.co.in
                            </a>
                        </div>
                        <div className="flex items-center gap-4">
                            <Globe size={18} className="text-clay" />
                            <a href="https://www.policy1.co.in" target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="text-sm text-moss transition-colors duration-300 hover:text-clay">
                                www.policy1.co.in
                            </a>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.1} className="lg:col-span-7">
                    <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label htmlFor="quote-name" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-moss">Your name</label>
                            <input
                                id="quote-name"
                                data-testid="quote-name-input"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Anand Kumar"
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label htmlFor="quote-phone" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-moss">Phone number</label>
                            <input
                                id="quote-phone"
                                data-testid="quote-phone-input"
                                required
                                type="tel"
                                pattern="[0-9+ ]{10,15}"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="98430 00000"
                                className={inputCls}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="quote-type" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-moss">I want to protect</label>
                            <select
                                id="quote-type"
                                data-testid="quote-type-select"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className={inputCls}
                            >
                                {["Life Insurance", "Health Insurance", "Motor Insurance", "Home Insurance", "Business Insurance", "Travel Insurance", "Not sure — advise me"].map((t) => (
                                    <option key={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="quote-message" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-moss">Anything we should know? (optional)</label>
                            <textarea
                                id="quote-message"
                                data-testid="quote-message-input"
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="E.g. family floater for 4, budget around ₹15,000/year"
                                className={`${inputCls} resize-none`}
                            />
                        </div>
                        <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                        <div className="md:col-span-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="quote-submit-btn"
                                className="group inline-flex items-center gap-3 rounded-full bg-ink px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-clay disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitting ? "Sending…" : "Request a Call Back"}
                                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                        </div>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
